import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MagnifyingGlass, Plus, Package } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useSortable, SortTh } from "@/components/SortableTable";
import { useAuth } from "@/lib/AuthContext";
import { AccessDenied } from "@/components/AccessDenied";

const PRICING_LABELS = { monthly: "Monthly", annual: "Annual", one_off: "One-off" };
const CAT_LABELS = { user_licenses: "User Licenses", modules: "Modules", services: "Services", support: "Support", optional_modules: "Modules", implementation_fees: "Implementation" };

export default function ProductsList() {
  const { user, canSee, canWrite } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const canWriteProducts = canWrite("products");

  useEffect(() => {
    api.get(`/mgmt/products${search ? `?search=${encodeURIComponent(search)}` : ""}`)
      .then(r => setProducts(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [search]);

  const { sorted, sortKey, sortDir, onSort } = useSortable(products, "name", "asc");

  // Gate on the new `products` module. Users with only `sales` access
  // can still pick products from inside QuoteBuilder (that call hits
  // /mgmt/products with the implicit-read rule), but the standalone
  // Products DB page is reserved for users with products ≥ read.
  if (user?.user_type === "internal" && !canSee("products")) return <AccessDenied module="Products" />;

  return (
    <div className="p-6" data-testid="products-list-page">
      <div className="flex items-center justify-start gap-6 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-[#0A0A0A] tracking-tight">Products</h1>
          <p className="text-sm text-[#A3A3A3] mt-0.5">{products.length} product{products.length !== 1 ? "s" : ""}</p>
        </div>
        {canWriteProducts && (
          <Link to="/mgmt/products/new" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055FF] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#003ECB]" data-testid="new-product-button">
            <Plus size={15} weight="bold" /> New Product
          </Link>
        )}
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
          <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="products-search" />
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E5E5E5] p-6 space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-[#F5F5F5] animate-pulse" />)}</div>
      ) : products.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] p-12 text-center">
          <Package size={32} className="text-[#E5E5E5] mx-auto mb-3" />
          <p className="text-[#A3A3A3] text-sm">No products yet</p>
          {canWriteProducts && (
            <Link to="/mgmt/products/new" className="text-[#0055FF] text-sm hover:underline mt-1 inline-block">Add your first product</Link>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5] overflow-hidden">
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
                <SortTh sortKey="name" label="Product" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortTh sortKey="product_code" label="Code" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortTh sortKey="category" label="Category" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortTh sortKey="current_selling_price" label="Price" current={sortKey} dir={sortDir} onSort={onSort} align="right" />
                <SortTh sortKey="pricing_type" label="Pricing" current={sortKey} dir={sortDir} onSort={onSort} />
                <SortTh sortKey="is_active" label="Status" current={sortKey} dir={sortDir} onSort={onSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {sorted.map(p => (
                <tr key={p.product_id} className={`hover:bg-[#FAFAFA] transition-colors ${canWriteProducts ? "cursor-pointer" : ""}`}
                    onClick={() => canWriteProducts && navigate(`/mgmt/products/${p.product_id}/edit`)} data-testid={`product-row-${p.product_id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-[#0A0A0A]">{p.name}</p>
                    {p.description && p.description !== p.name && <p className="text-[10px] text-[#A3A3A3] truncate max-w-xs">{p.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-[#525252] font-mono text-xs">{p.product_code}</td>
                  <td className="px-4 py-3 text-[#525252] text-xs">{CAT_LABELS[p.category] || p.category}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#0A0A0A]">{p.price_currency_code} {p.current_selling_price?.toFixed(2)}</td>
                  <td className="px-4 py-3 text-[#525252] text-xs">{PRICING_LABELS[p.pricing_type] || p.pricing_type}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-bold ${p.is_active ? "bg-emerald-50 text-emerald-700" : "bg-[#F5F5F5] text-[#A3A3A3]"}`}>{p.is_active ? "Active" : "Inactive"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
